import { checkLabelTranslation } from './checkLabelTranslation.js';

/**
 * Создает фабрику слотов для инстанса с мемоизацией результатов
 * @param {Object} instance - объект инстанса
 * @param {Object} options - дополнительные опции (currency, areaUnit, showStatus, showPrice, hidePriceByStatus)
 * @param {Object} formatters - объект с функциями форматирования (formatNumber, formatCurrency, formatPlural, translate)
 * @returns {Object} - объект с методами для получения данных слотов
 */
export function createInstanceSlotFactory(instance, options = {}, formatters) {
  const { showStatus, showPrice, hidePriceByStatus = true } = options;
  const { formatNumber, formatCurrency, formatPlural, translate } = formatters;

  const cache = new Map();

  const memoize = (key, fn) => {
    return () => {
      if (!cache.has(key)) {
        cache.set(key, fn());
      }
      return cache.get(key);
    };
  };

  const getPrice = memoize('price', () => {
    if (
      (instance?.status?.type !== 1 && hidePriceByStatus == true) ||
      showPrice === false
    ) {
      return null;
    }
    return instance.price;
  });

  const getExtraPrice = memoize('extraPrice', () => {
    if (
      (instance?.status?.type !== 1 && hidePriceByStatus == true) ||
      showPrice === false
    ) {
      return null;
    }
    return instance.extra_price ?? 0;
  });

  const getOriginalPrice = memoize('originalPrice', () => {
    if (
      (instance?.status?.type !== 1 && hidePriceByStatus == true) ||
      showPrice === false
    ) {
      return null;
    }
    return instance.old_price;
  });

  const getRooms = memoize('rooms', () => {
    if (instance?.layout?.type == null) {
      return null;
    }
    const key = instance?.layout?.label;
    if (key == null) {
      return null;
    }
    if (instance?.layout?.type !== 'living') {
      return null;
    }
    return key;
  });

  const getTotalArea = memoize('totalArea', () => {
    return instance?.layout?.area_total || null;
  });

  const getPricePerTotalArea = memoize('pricePerTotalArea', () => {
    const totalArea = getTotalArea();
    const price = getPrice();
    return totalArea && price ? Math.round(price / totalArea) : null;
  });

  // Слоты
  const getStoreySlotItem = memoize('storeySlotItem', () => {
    let value = instance.storey.number;
    if (instance.estate.type === 2) {
      value = null;
    }

    return {
      value: value,
      render: value ? value + ' ' + translate('storey') : null,
    };
  });

  const getSectionSlotItem = memoize('sectionSlotItem', () => {
    let value = instance.section.number;
    if (instance.estate.type === 2) {
      value = null;
    }
    return {
      value: value,
      render: value ? translate('section') + ' ' + value : null,
    };
  });

  const getEstateSlotItem = memoize('estateSlotItem', () => {
    let value = instance.estate.name;

    return {
      value: value,
      render: value ?? null,
    };
  });

  const getTypeSlotItem = memoize('typeSlotItem', () => {
    let value = instance?.layout?.type;

    if (instance.estate.type === 2) {
      value = 'house';
    }

    return {
      value: value,
      render: value ? translate('typeMedium', value) : null,
      slots: {
        short: value ? translate('typeShort', value) : null,
        full: value ? translate('typeFull', value) : null,
      },
    };
  });

  const getPriceSlotItem = memoize('priceSlotItem', () => {
    const price = getPrice();
    const originalPrice = getOriginalPrice();
    const extraPrice = getExtraPrice();

    const render = price ? formatCurrency(price + extraPrice) : null;

    let extendedRender = render;

    if (
      originalPrice !== price &&
      price !== null &&
      originalPrice &&
      originalPrice !== null
    ) {
      extendedRender = `<div class="price-slot"><div class="price-slot-cross"> ${formatCurrency(
        originalPrice + extraPrice
      )}</div><div class="price-slot-actual">${render}</div></div>`;
    }

    return {
      value: price,
      render: render,
      slots: {
        extended: extendedRender,
        chessPlusBody: extendedRender,
        tooltipBody: extendedRender,
        previewCardBody: extendedRender,
        instanceDetailsHeader: extendedRender,
      },
    };
  });

  const getStatusSlotItem = memoize('statusSlotItem', () => {
    if (showStatus === false) {
      return {
        value: null,
        render: null,
      };
    }
    return {
      value: instance.status,
      render: instance.status?.name,
    };
  });

  const getNumberSlotItem = memoize('numberSlotItem', () => {
    return {
      value: instance.number,
      render: translate('numberSymbol') + ' ' + instance.number,
      slots: {
        short: instance.number,
        chessCommonBody: instance.number,
      },
    };
  });

  const getRoomsSlotItem = memoize('roomsSlotItem', () => {
    const value = getRooms();
    let render = null;
    let chessCommonRender = null;
    if (value !== null) {
      if (checkLabelTranslation(value)) {
        render = translate('roomsField', value);
        chessCommonRender = translate('roomsShort', value);
      } else {
        render = value + ' ' + formatPlural(value, translate('roomsFull'));
        chessCommonRender = value;
      }
    }
    return {
      value: value,
      render: render,
      slots: {
        translateShort: translate('roomsShort', value),
        short: chessCommonRender,
        chessCommonBody: chessCommonRender,
      },
    };
  });

  const getLabelSlotItem = memoize('labelSlotItem', () => {
    let value = getRooms();

    if (instance?.layout?.type !== 'living') {
      value = instance?.layout?.label;
      return {
        value: value,
        render: instance?.layout?.type
          ? translate('typeMedium', instance?.layout?.type)
          : null,
        slots: {
          translateShort: instance?.layout?.type
            ? translate('typeShort', instance?.layout?.type)
            : null,
          short: instance?.layout?.type
            ? translate('typeShort', instance?.layout?.type)
            : null,
          chessCommonBody: instance?.layout?.type
            ? translate('typeShort', instance?.layout?.type)
            : null,
        },
      };
    }
    return getRoomsSlotItem();
  });

  const getTotalAreaSlotItem = memoize('totalAreaSlotItem', () => {
    const totalArea = getTotalArea();
    return {
      value: totalArea,
      render: totalArea
        ? `${formatNumber(totalArea)} ${translate('areaUnit')}`
        : null,
    };
  });

  const getPricePerTotalAreaSlotItem = memoize(
    'pricePerTotalAreaSlotItem',
    () => {
      const value = getPricePerTotalArea();
      return {
        value: value,
        render: value
          ? `${formatCurrency(value)}/${translate('areaUnit')}`
          : null,
      };
    }
  );

  const getTotalAreaWithPriceSlotItem = memoize(
    'totalAreaWithPriceSlotItem',
    () => {
      const value = getTotalArea();
      const price = getPricePerTotalArea();
      let render = null;
      if (value && price) {
        render = `${
          getTotalAreaSlotItem().render
        } <span style='white-space: nowrap'>~ ${
          getPricePerTotalAreaSlotItem().render
        }</span>`;
      } else if (value) {
        render = getTotalAreaSlotItem().render;
      }

      return {
        value: value,
        render: render,
      };
    }
  );

  // Объект с методами для получения данных слотов
  const slotData = {
    price: getPriceSlotItem,
    priceOrStatus: () =>
      getPrice() ? getPriceSlotItem() : getStatusSlotItem(),
    status: getStatusSlotItem,
    rooms: getRoomsSlotItem,
    label: getLabelSlotItem,
    number: getNumberSlotItem,
    totalArea: getTotalAreaSlotItem,
    pricePerTotalArea: getPricePerTotalAreaSlotItem,
    totalAreaWithPrice: getTotalAreaWithPriceSlotItem,
    type: getTypeSlotItem,
    storey: getStoreySlotItem,
    estate: getEstateSlotItem,
    section: getSectionSlotItem,
  };

  // Методы для работы со слотами
  const hasContent = (template) => {
    if (template == null) {
      return false;
    }
    if (!slotData[template]) {
      return false;
    }
    const result = slotData[template]();

    if (result.value !== null) {
      return true;
    }
    return false;
  };

  const renderSlot = (slot, template) => {
    if (template == null) {
      return null;
    }
    if (!slotData[template]) {
      return null;
    }
    const result = slotData[template]();
    if (result.slots && result.slots[slot]) {
      return result.slots[slot];
    }
    return result.render;
  };

  // Метод для очистки кэша при изменении данных
  const clearCache = () => {
    cache.clear();
  };

  return {
    slotData,
    hasContent,
    renderSlot,
    clearCache,
  };
}
